// src/pages/CompanyPages/ManageAppointments.js

import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  MenuItem,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useParams } from "react-router-dom";
import { format } from "date-fns";
import Swal from "sweetalert2";

import axiosInstance from "../../Services/axiosConfig";
import Sidebar from "../../components/CompanySidebar";
import Footer from "../../components/Footer";
import FeedbackButton from "../../components/FeedbackButton";
import FeedbackSnackbar from "../../components/FeedbackSnackbar";
import LoadingIndicator from "../../components/LoadingIndicator";
import { useThemeMode } from "../../components/ThemeModeContext";

export default function ManageAppointments() {
  const { companyId } = useParams();
  const token = localStorage.getItem("authToken");

  // Estados de setores, serviços e agendamentos
  const [sectors, setSectors] = useState([]);
  const [selectedSector, setSelectedSector] = useState("");
  const [services, setServices] = useState([]);
  const [filteredAppointments, setFilteredAppointments] = useState([]);

  // Estado para "edição" ou "criação" de agendamento
  const [selectedAppointment, setSelectedAppointment] = useState(null);

  // Estado de carregamento
  const [loading, setLoading] = useState(false);

  // Formulário
  const [formData, setFormData] = useState({
    service_id: "",
    user_name: "",
    user_email: "",
    date: "",
    quantity: 1, // Nova regra: definir quantidade
  });

  // Estados para Snackbar (caso queira exibir mensagens de erro/sucesso)
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // Modo Dark/Light
  const { mode } = useThemeMode();
  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          background: {
            default: mode === "light" ? "#FAF5E4" : "#121212",
            paper: mode === "light" ? "#FFFFFF" : "#1E1E1E",
          },
          text: {
            primary: mode === "light" ? "#1A202C" : "#FFFFFF",
          },
        },
      }),
    [mode]
  );

  // Fecha snackbar
  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  // Busca setores
  const fetchSectors = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`sectors/${token}/${companyId}/`);
      if (response.data.status === "success") {
        const sectorList = Object.entries(response.data.data).map(([id, sector]) => ({
          id,
          ...sector,
        }));
        setSectors(sectorList);
      }
    } catch (error) {
      console.error("Error fetching sectors", error);
      setOpenSnackbar(true);
      setSnackbarMsg("Error fetching sectors");
      setSnackbarSeverity("error");
    } finally {
      setLoading(false);
    }
  };

  // Busca serviços de um setor
  const fetchServices = async (sectorId) => {
    if (!sectorId) {
      setServices([]);
      return;
    }
    setLoading(true);
    try {
      const response = await axiosInstance.get(`services/${token}/${companyId}/${sectorId}/`);
      if (response.data.status === "success") {
        const serviceList = Object.entries(response.data.data).map(([id, service]) => ({
          id,
          ...service,
        }));
        setServices(serviceList);
      } else {
        setServices([]);
      }
    } catch (error) {
      console.error("Error fetching services", error);
      setServices([]);
    } finally {
      setLoading(false);
    }
  };

  // Busca agendamentos de um setor
  const fetchSectorAppointments = async (sectorId) => {
    if (!sectorId) {
      setFilteredAppointments([]);
      return;
    }
    setLoading(true);
    try {
      const response = await axiosInstance.get(
        `sector-appointments/${token}/${sectorId}/${companyId}/`
      );
      if (response.data.status === "success") {
        const appointmentList = Object.entries(response.data.data).map(([id, appointment]) => ({
          id,
          ...appointment,
        }));
        setFilteredAppointments(appointmentList);
      } else {
        setFilteredAppointments([]);
      }
    } catch (error) {
      console.error("Error fetching sector appointments", error);
      setFilteredAppointments([]);
    } finally {
      setLoading(false);
    }
  };

  // Efeito inicial: buscar setores
  useEffect(() => {
    fetchSectors();
  }, [companyId, token]);

  // Efeito ao trocar de setor
  useEffect(() => {
    if (selectedSector) {
      fetchServices(selectedSector);
      fetchSectorAppointments(selectedSector);
    } else {
      setServices([]);
      setFilteredAppointments([]);
    }
  }, [selectedSector]);

  // Lida com mudanças no formulário
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Ao clicar num appointment (para editar)
  const handleAppointmentSelect = (appointment) => {
    setSelectedAppointment(appointment);
    setFormData({
      service_id: appointment.service_id || "",
      user_name: appointment.client || "",
      user_email: appointment.email || "",
      date: appointment.date.replace(" ", "T"),
      quantity: appointment.quantity || 1, // Adicionando quantity
    });
  };

  // Cria / Edita
  const handleSubmit = async () => {
    setLoading(true);
    try {
      const url = selectedAppointment
        ? `company-appointments/${token}/${companyId}/${selectedAppointment.id}/`
        : `company-appointments/${token}/${companyId}/`;

      const response = await axiosInstance.post(url, formData);

      if (response.data.status === "success") {
        Swal.fire(
          "Success",
          selectedAppointment
            ? "Appointment updated successfully"
            : "Appointment created successfully",
          "success"
        );
        setFormData({ service_id: "", user_name: "", user_email: "", date: "", quantity: 1 });
        setSelectedAppointment(null);
        fetchSectorAppointments(selectedSector);
      } else {
        Swal.fire("Error", response.data.message, "error");
      }
    } catch (error) {
      console.error("Error creating/editing appointment", error);
      Swal.fire("Error", "An error occurred. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Box display="flex" flexDirection="column" minHeight="100vh" bgcolor="background.default">
        <Box display="flex" flexGrow={1}>
          <Sidebar companyId={companyId} />
          <Box flex={1} p={3} color="text.primary">
            <Typography variant="h4" gutterBottom>
              Manage Appointments
            </Typography>

            {/* Campo novo: quantidade */}
            <TextField
              label="Quantity"
              name="quantity"
              type="number"
              value={formData.quantity}
              onChange={handleChange}
              fullWidth
              required
              margin="normal"
            />

            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={loading}
              fullWidth
              sx={{ mt: 2 }}
            >
              {selectedAppointment ? "Update Appointment" : "Create Appointment"}
            </Button>
          </Box>
        </Box>
        <FeedbackButton />
        <Footer />
      </Box>
    </ThemeProvider>
  );
}
